import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	ArrowLeft,
	Building2,
	CheckCircle2,
	Circle,
	Download,
	Eye,
	FileText,
	Loader2,
	Phone,
	User,
} from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTRPC } from "@/integrations/trpc/react";
import { useAuth } from "@/lib/auth";
import { getBidStatusColor } from "@/lib/utils";

export const Route = createFileRoute("/vendors/$id")({
	component: VendorDetailPage,
});

interface VendorProfile {
	id: string;
	user_id: string;
	external_user_id: string;
	first_name: string | null;
	last_name: string | null;
	phone_number: string | null;
	company: string | null;
	external_org_id: string | null;
	external_org_name: string | null;
}

interface VendorEngagement {
	id: string;
	vendor_profile_id: string;
	first_visited_at: string;
	last_visited_at: string;
	visit_count: number;
	vendor_profiles: VendorProfile | null;
}

function VendorDetailPage() {
	const { id } = Route.useParams(); // This is vendor_profile_id
	const { user } = useAuth();
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const [activeTab, setActiveTab] = useState("overview");
	const [viewingFormId, setViewingFormId] = useState<string | null>(null);
	const [downloadingFormId, setDownloadingFormId] = useState<string | null>(null);

	// Query vendor engagement by profile ID
	const { data: engagementData, isLoading: vendorLoading } = useQuery(
		trpc.vendorManagement.engagements.getByVendor.queryOptions({
			buyerOrgId: user?.organizationId ?? "",
			vendorProfileId: id,
		}),
	);

	// Query vendor onboarding progress
	const { data: onboardingProgress, isLoading: progressLoading } = useQuery(
		trpc.vendorManagement.engagements.getVendorOnboardingProgress.queryOptions({
			buyerOrgId: user?.organizationId ?? "",
			vendorProfileId: id,
		}),
	);

	// Query vendor form submissions
	const { data: formSubmissionsData, isLoading: formSubmissionsLoading } = useQuery(
		trpc.vendorManagement.vendorSubmissions.getFormSubmissions.queryOptions({
			buyerOrgId: user?.organizationId ?? "",
			vendorProfileId: id,
		}),
	);

	// Query vendor document submissions
	const { data: docSubmissionsData, isLoading: docSubmissionsLoading } = useQuery(
		trpc.vendorManagement.vendorSubmissions.getDocumentSubmissions.queryOptions({
			buyerOrgId: user?.organizationId ?? "",
			vendorProfileId: id,
		}),
	);

	// Query vendor bids
	const { data: vendorBidsData, isLoading: vendorBidsLoading } = useQuery(
		trpc.vendorManagement.vendorSubmissions.getVendorBids.queryOptions({
			buyerOrgId: user?.organizationId ?? "",
			vendorProfileId: id,
		}),
	);

	const engagement = engagementData as VendorEngagement | undefined;
	const vendor = engagement?.vendor_profiles;

	// Mutation for generating filled PDF
	const generateFilledPdfMutation = useMutation(
		trpc.pdfTemplates.generateFilledPdfFromSubmission.mutationOptions(),
	);

	const isLoading = vendorLoading || progressLoading || formSubmissionsLoading || docSubmissionsLoading || vendorBidsLoading;

	if (isLoading) {
		return (
			<DashboardLayout>
				<div className="flex items-center justify-center h-64">
					<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				</div>
			</DashboardLayout>
		);
	}

	if (!engagement || !vendor) {
		return (
			<DashboardLayout>
				<div className="text-center py-12">
					<h2 className="text-2xl font-bold">Vendor not found</h2>
					<Button asChild className="mt-4">
						<Link to="/vendors">Back to Vendors</Link>
					</Button>
				</div>
			</DashboardLayout>
		);
	}

	const companyName = vendor.company || vendor.external_org_name || "Unknown Company";
	const contactName = `${vendor.first_name ?? ""} ${vendor.last_name ?? ""}`.trim() || "Unknown Contact";
	const companyInitials = companyName
		.split(" ")
		.map((n) => n[0])
		.join("")
		.slice(0, 2)
		.toUpperCase();

	// Get actual onboarding status from progress data
	const onboardingStatus = onboardingProgress?.status ?? "not_started";

	// View handler for form PDFs - opens in new tab
	const handleViewFormPdf = async (submissionId: string | null) => {
		if (!submissionId) {
			toast.error("No submission found for this form");
			return;
		}

		setViewingFormId(submissionId);
		try {
			const result = await generateFilledPdfMutation.mutateAsync({
				submissionId,
			});

			if (result?.pdfBase64) {
				const byteCharacters = atob(result.pdfBase64);
				const byteNumbers = new Array(byteCharacters.length);
				for (let i = 0; i < byteCharacters.length; i++) {
					byteNumbers[i] = byteCharacters.charCodeAt(i);
				}
				const byteArray = new Uint8Array(byteNumbers);
				const blob = new Blob([byteArray], { type: "application/pdf" });
				const url = URL.createObjectURL(blob);
				window.open(url, "_blank");
			} else {
				toast.error("Failed to generate PDF for viewing");
			}
		} catch (error) {
			console.error("View error:", error);
			toast.error("Failed to view form PDF");
		} finally {
			setViewingFormId(null);
		}
	};

	// Download handler for form PDFs - generates PDF with field values applied
	const handleDownloadFormPdf = async (submissionId: string | null, title: string) => {
		if (!submissionId) {
			toast.error("No submission found for this form");
			return;
		}

		setDownloadingFormId(submissionId);
		try {
			// Generate filled PDF on the fly
			const result = await generateFilledPdfMutation.mutateAsync({
				submissionId,
			});

			if (result?.pdfBase64) {
				// Convert base64 to blob and download
				const byteCharacters = atob(result.pdfBase64);
				const byteNumbers = new Array(byteCharacters.length);
				for (let i = 0; i < byteCharacters.length; i++) {
					byteNumbers[i] = byteCharacters.charCodeAt(i);
				}
				const byteArray = new Uint8Array(byteNumbers);
				const blob = new Blob([byteArray], { type: "application/pdf" });

				// Create download link
				const url = URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = url;
				link.download = result.filename || `${title} - Filled.pdf`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);
			} else {
				toast.error("Failed to generate filled PDF");
			}
		} catch (error) {
			console.error("Download error:", error);
			toast.error("Failed to download form PDF");
		} finally {
			setDownloadingFormId(null);
		}
	};

	// Download handler for uploaded documents
	const handleDownloadDocument = async (filePath: string, _title: string) => {
		try {
			const result = await queryClient.fetchQuery(
				trpc.vendorManagement.vendorSubmissions.getDocumentDownloadUrl.queryOptions({ filePath })
			);
			if (result?.signedUrl) {
				window.open(result.signedUrl, "_blank");
			} else {
				toast.error("Failed to get download URL");
			}
		} catch (error) {
			toast.error("Failed to download document");
		}
	};

	// Build form submissions list for display
	const formSubmissionsList = (formSubmissionsData?.orgForms ?? []).map(form => {
		const submission = (formSubmissionsData?.submissions ?? []).find(
			s => s.org_form_id === form.id
		);
		return {
			id: form.id,
			title: form.title,
			isRequired: form.is_required,
			isCompleted: !!submission,
			submittedAt: submission?.submitted_at,
			submissionId: submission?.id ?? null,
			isSigned: submission?.is_signed ?? false,
		};
	});

	// Build document submissions list for display
	const documentSubmissionsList = (docSubmissionsData?.requiredDocs ?? []).map(doc => {
		const submission = (docSubmissionsData?.submissions ?? []).find(
			s => s.org_required_document_id === doc.id
		);
		return {
			id: doc.id,
			title: doc.title,
			isRequired: doc.is_required,
			isUploaded: !!submission,
			uploadedAt: submission?.uploaded_at,
			filePath: submission?.file_path ?? null,
			originalFilename: submission?.original_filename ?? null,
		};
	});

	// Map vendor bids data for display
	const vendorBids = (vendorBidsData ?? []).map(bid => ({
		id: bid.id,
		solicitationId: bid.solicitationId,
		solicitationTitle: bid.solicitationTitle,
		submittedAt: bid.submittedAt ?? "",
		amount: bid.totalPrice ? `$${Number(bid.totalPrice).toLocaleString()}` : "-",
		status: bid.status,
		score: null as number | null, // Score would come from evaluation, not bid itself
	}));
	const vendorAwards: Array<{
		id: string;
		solicitationTitle: string;
		awardDate: string;
		contractValue: string;
		contractStartDate: string;
		contractEndDate: string;
		status: string;
	}> = [];

	return (
		<DashboardLayout>
			<div className="space-y-6">
				{/* Header */}
				<div className="flex items-start justify-between">
					<div className="space-y-1">
						<div className="flex items-center gap-2">
							<Button variant="ghost" size="icon" asChild>
								<Link to="/vendors">
									<ArrowLeft className="h-4 w-4" />
								</Link>
							</Button>
							{onboardingStatus === "complete" ? (
								<Badge className="bg-green-100 text-green-700">
									<CheckCircle2 className="h-3 w-3 mr-1" />
									Onboarding Complete
								</Badge>
							) : onboardingStatus === "in_progress" ? (
								<Badge className="bg-yellow-100 text-yellow-700">
									<Circle className="h-3 w-3 mr-1" />
									Onboarding In Progress
								</Badge>
							) : (
								<Badge variant="outline" className="text-muted-foreground">
									<Circle className="h-3 w-3 mr-1" />
									Not Started
								</Badge>
							)}
						</div>
						<div className="flex items-center gap-4">
							<Avatar className="h-16 w-16">
								<AvatarFallback className="text-xl">
									{companyInitials}
								</AvatarFallback>
							</Avatar>
							<div>
								<h1 className="text-3xl font-bold tracking-tight">
									{companyName}
								</h1>
								<p className="text-muted-foreground flex items-center gap-1">
									<User className="h-4 w-4" />
									{contactName}
								</p>
							</div>
						</div>
					</div>
					<div className="flex gap-2">
						{vendor.phone_number && (
							<Button
								variant="outline"
								onClick={() => window.open(`tel:${vendor.phone_number}`)}
							>
								<Phone className="mr-2 h-4 w-4" />
								Call
							</Button>
						)}
					</div>
				</div>

				{/* Quick Stats */}
				<div className="grid gap-4 md:grid-cols-3">
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<FileText className="h-4 w-4 text-muted-foreground" />
								<span className="text-sm text-muted-foreground">
									Forms Completed
								</span>
							</div>
							<p className="text-2xl font-bold mt-1">
								{onboardingProgress?.forms.completed ?? 0} / {onboardingProgress?.forms.total ?? 0}
							</p>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<FileText className="h-4 w-4 text-muted-foreground" />
								<span className="text-sm text-muted-foreground">
									Documents Uploaded
								</span>
							</div>
							<p className="text-2xl font-bold mt-1">
								{onboardingProgress?.documents.completed ?? 0} / {onboardingProgress?.documents.total ?? 0}
							</p>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<CheckCircle2 className="h-4 w-4 text-muted-foreground" />
								<span className="text-sm text-muted-foreground">
									Compliance Checks
								</span>
							</div>
							<p className="text-2xl font-bold mt-1">
								{onboardingProgress?.compliance.completed ?? 0} / {onboardingProgress?.compliance.total ?? 0}
							</p>
						</CardContent>
					</Card>
				</div>

				{/* Tabs */}
				<Tabs value={activeTab} onValueChange={setActiveTab}>
					<TabsList>
						<TabsTrigger value="overview">Overview</TabsTrigger>
						<TabsTrigger value="bids">Bids ({vendorBids.length})</TabsTrigger>
						<TabsTrigger value="awards">
							Awards ({vendorAwards.length})
						</TabsTrigger>
						<TabsTrigger value="documents">Documents</TabsTrigger>
					</TabsList>

					<TabsContent value="overview" className="space-y-6">
						<div className="grid gap-6 md:grid-cols-2">
							<Card>
								<CardHeader>
									<CardTitle>Company Information</CardTitle>
								</CardHeader>
								<CardContent className="space-y-4">
									<div className="flex items-start gap-3">
										<Building2 className="h-5 w-5 text-muted-foreground mt-0.5" />
										<div>
											<p className="text-sm text-muted-foreground">Company Name</p>
											<p className="font-medium">{companyName}</p>
										</div>
									</div>
									{vendor.external_org_name && vendor.company && vendor.external_org_name !== vendor.company && (
										<div className="flex items-start gap-3">
											<Building2 className="h-5 w-5 text-muted-foreground mt-0.5" />
											<div>
												<p className="text-sm text-muted-foreground">
													Bidscope Organization
												</p>
												<p className="font-medium">{vendor.external_org_name}</p>
											</div>
										</div>
									)}
								</CardContent>
							</Card>

							<Card>
								<CardHeader>
									<CardTitle>Contact Information</CardTitle>
								</CardHeader>
								<CardContent className="space-y-4">
									<div className="flex items-start gap-3">
										<User className="h-5 w-5 text-muted-foreground mt-0.5" />
										<div>
											<p className="text-sm text-muted-foreground">Contact Name</p>
											<p className="font-medium">{contactName}</p>
										</div>
									</div>
									{vendor.phone_number && (
										<div className="flex items-start gap-3">
											<Phone className="h-5 w-5 text-muted-foreground mt-0.5" />
											<div>
												<p className="text-sm text-muted-foreground">Phone</p>
												<a href={`tel:${vendor.phone_number}`} className="font-medium text-primary hover:underline">
													{vendor.phone_number}
												</a>
											</div>
										</div>
									)}
								</CardContent>
							</Card>
						</div>

						<Card>
							<CardHeader>
								<CardTitle>Onboarding Progress</CardTitle>
								<CardDescription>
									Track this company's onboarding completion status
								</CardDescription>
							</CardHeader>
							<CardContent>
								{onboardingProgress ? (
									<div className="space-y-4">
										<div className="flex items-center justify-between">
											<span className="text-sm font-medium">Overall Progress</span>
											<span className="text-sm text-muted-foreground">
												{onboardingProgress.overall.percentage}%
											</span>
										</div>
										<div className="h-2 bg-muted rounded-full overflow-hidden">
											<div
												className="h-full bg-primary transition-all"
												style={{ width: `${onboardingProgress.overall.percentage}%` }}
											/>
										</div>
										<div className="grid gap-2 text-sm">
											<div className="flex justify-between">
												<span>Forms</span>
												<span className="text-muted-foreground">
													{onboardingProgress.forms.completed} / {onboardingProgress.forms.total}
												</span>
											</div>
											<div className="flex justify-between">
												<span>Documents</span>
												<span className="text-muted-foreground">
													{onboardingProgress.documents.completed} / {onboardingProgress.documents.total}
												</span>
											</div>
											<div className="flex justify-between">
												<span>Compliance Checks</span>
												<span className="text-muted-foreground">
													{onboardingProgress.compliance.completed} / {onboardingProgress.compliance.total}
												</span>
											</div>
										</div>
									</div>
								) : (
									<p className="text-muted-foreground text-sm">
										Loading onboarding progress...
									</p>
								)}
							</CardContent>
						</Card>
					</TabsContent>

					<TabsContent value="bids">
						<Card>
							<CardHeader>
								<CardTitle>Bid History</CardTitle>
								<CardDescription>
									All bids submitted by this vendor
								</CardDescription>
							</CardHeader>
							<CardContent>
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>Solicitation</TableHead>
											<TableHead>Submitted</TableHead>
											<TableHead>Amount</TableHead>
											<TableHead>Status</TableHead>
											<TableHead>Score</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{vendorBids.map((bid) => (
											<TableRow key={bid.id}>
												<TableCell className="font-medium">
													<Link
														to="/solicitations/$id"
														params={{ id: bid.solicitationId }}
														className="hover:underline"
													>
														{bid.solicitationTitle}
													</Link>
												</TableCell>
												<TableCell>
													{bid.submittedAt ? new Date(bid.submittedAt).toLocaleDateString() : "-"}
												</TableCell>
												<TableCell>{bid.amount}</TableCell>
												<TableCell>
													<Badge className={getBidStatusColor(bid.status)}>
														{bid.status.replace("_", " ")}
													</Badge>
												</TableCell>
												<TableCell>
													{bid.score !== null ? `${bid.score}/100` : "-"}
												</TableCell>
											</TableRow>
										))}
										{vendorBids.length === 0 && (
											<TableRow>
												<TableCell
													colSpan={5}
													className="text-center py-8 text-muted-foreground"
												>
													No bids submitted
												</TableCell>
											</TableRow>
										)}
									</TableBody>
								</Table>
							</CardContent>
						</Card>
					</TabsContent>

					<TabsContent value="awards">
						<Card>
							<CardHeader>
								<CardTitle>Award History</CardTitle>
								<CardDescription>
									Contracts awarded to this vendor
								</CardDescription>
							</CardHeader>
							<CardContent>
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>Solicitation</TableHead>
											<TableHead>Award Date</TableHead>
											<TableHead>Contract Value</TableHead>
											<TableHead>Contract Period</TableHead>
											<TableHead>Status</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{vendorAwards.map((award) => (
											<TableRow key={award.id}>
												<TableCell className="font-medium">
													{award.solicitationTitle}
												</TableCell>
												<TableCell>
													{new Date(award.awardDate).toLocaleDateString()}
												</TableCell>
												<TableCell>{award.contractValue}</TableCell>
												<TableCell>
													{new Date(
														award.contractStartDate,
													).toLocaleDateString()}{" "}
													-{" "}
													{new Date(award.contractEndDate).toLocaleDateString()}
												</TableCell>
												<TableCell>
													<Badge
														variant={
															award.status === "active"
																? "default"
																: "secondary"
														}
													>
														{award.status}
													</Badge>
												</TableCell>
											</TableRow>
										))}
										{vendorAwards.length === 0 && (
											<TableRow>
												<TableCell
													colSpan={5}
													className="text-center py-8 text-muted-foreground"
												>
													No awards
												</TableCell>
											</TableRow>
										)}
									</TableBody>
								</Table>
							</CardContent>
						</Card>
					</TabsContent>

					<TabsContent value="documents" className="space-y-6">
						{/* Completed Forms */}
						<Card>
							<CardHeader>
								<CardTitle>Completed Forms</CardTitle>
								<CardDescription>
									Forms submitted by this vendor during onboarding
								</CardDescription>
							</CardHeader>
							<CardContent>
								<div className="space-y-2">
									{formSubmissionsList.length === 0 ? (
										<p className="text-muted-foreground text-sm">No forms required or submitted.</p>
									) : (
										formSubmissionsList.map((form) => (
											<div
												key={form.id}
												className="flex items-center justify-between p-3 border rounded-lg"
											>
												<div className="flex items-center gap-3">
													<FileText className="h-5 w-5 text-muted-foreground" />
													<div>
														<p className="font-medium">
															{form.title}
															{form.isRequired && (
																<span className="text-destructive ml-1">*</span>
															)}
														</p>
														<p className="text-sm text-muted-foreground">
															{form.isCompleted
																? `Submitted: ${new Date(form.submittedAt!).toLocaleDateString()}`
																: "Not submitted"}
															{form.isSigned && " • Signed"}
														</p>
													</div>
												</div>
												<div className="flex items-center gap-2">
													<Badge
														variant={form.isCompleted ? "default" : "secondary"}
														className={form.isCompleted ? "bg-green-100 text-green-700" : ""}
													>
														{form.isCompleted ? "Completed" : "Pending"}
													</Badge>
													{form.isCompleted && form.submissionId && (
														<>
															<Tooltip>
																<TooltipTrigger asChild>
																	<Button
																		variant="ghost"
																		size="sm"
																		onClick={() => handleViewFormPdf(form.submissionId)}
																		disabled={viewingFormId === form.submissionId}
																	>
																		{viewingFormId === form.submissionId ? (
																			<Loader2 className="h-4 w-4 animate-spin" />
																		) : (
																			<Eye className="h-4 w-4" />
																		)}
																	</Button>
																</TooltipTrigger>
																<TooltipContent>View form</TooltipContent>
															</Tooltip>
															<Tooltip>
																<TooltipTrigger asChild>
																	<Button
																		variant="ghost"
																		size="sm"
																		onClick={() => handleDownloadFormPdf(form.submissionId, form.title)}
																		disabled={downloadingFormId === form.submissionId}
																	>
																		{downloadingFormId === form.submissionId ? (
																			<Loader2 className="h-4 w-4 animate-spin" />
																		) : (
																			<Download className="h-4 w-4" />
																		)}
																	</Button>
																</TooltipTrigger>
																<TooltipContent>Download form</TooltipContent>
															</Tooltip>
														</>
													)}
												</div>
											</div>
										))
									)}
								</div>
							</CardContent>
						</Card>

						{/* Uploaded Documents */}
						<Card>
							<CardHeader>
								<CardTitle>Uploaded Documents</CardTitle>
								<CardDescription>
									Required documents uploaded by this vendor
								</CardDescription>
							</CardHeader>
							<CardContent>
								<div className="space-y-2">
									{documentSubmissionsList.length === 0 ? (
										<p className="text-muted-foreground text-sm">No documents required or uploaded.</p>
									) : (
										documentSubmissionsList.map((doc) => (
											<div
												key={doc.id}
												className="flex items-center justify-between p-3 border rounded-lg"
											>
												<div className="flex items-center gap-3">
													<FileText className="h-5 w-5 text-muted-foreground" />
													<div>
														<p className="font-medium">
															{doc.title}
															{doc.isRequired && (
																<span className="text-destructive ml-1">*</span>
															)}
														</p>
														<p className="text-sm text-muted-foreground">
															{doc.isUploaded
																? `Uploaded: ${new Date(doc.uploadedAt!).toLocaleDateString()}${doc.originalFilename ? ` • ${doc.originalFilename}` : ""}`
																: "Not uploaded"}
														</p>
													</div>
												</div>
												<div className="flex items-center gap-2">
													<Badge
														variant={doc.isUploaded ? "default" : "secondary"}
														className={doc.isUploaded ? "bg-green-100 text-green-700" : ""}
													>
														{doc.isUploaded ? "Uploaded" : "Pending"}
													</Badge>
													{doc.isUploaded && doc.filePath && (
														<Tooltip>
															<TooltipTrigger asChild>
																<Button
																	variant="ghost"
																	size="sm"
																	onClick={() => handleDownloadDocument(doc.filePath!, doc.title)}
																>
																	<Download className="h-4 w-4" />
																</Button>
															</TooltipTrigger>
															<TooltipContent>Download: {doc.title}</TooltipContent>
														</Tooltip>
													)}
												</div>
											</div>
										))
									)}
								</div>
							</CardContent>
						</Card>
					</TabsContent>
				</Tabs>
			</div>
		</DashboardLayout>
	);
}
